import { fabric } from 'fabric';
import { createRoot, type Root } from 'react-dom/client';
import {
  isSimpleGroup,
  hasEmbeddedTextBehaviors,
  isShapeImage,
  isPresentationFrame,
  isFrame,
} from './guards';
import { Context } from '@aha-app/svgcanvas';
import { type Constructor, type Rect } from '../types';
import type { IPoint } from 'fabric/fabric-impl';
import { type TransformMatrix } from '../utils/zoom';

const MAX_IMAGE_LOAD_WAIT_TIME = 10000; // 10 seconds timeout to wait for all images to load

export enum ExportMode {
  NONE,
  SVG,
  PNG,
  HTML,
  HTML_WITHOUT_AHA_VIEWS,
}

type WhiteboardCanvas = fabric.Canvas & {
  exporting: ExportMode;
  _renderObjects(ctx: CanvasRenderingContext2D, objects: fabric.Object[]);
};

type RenderToPNGResult =
  | {
      success: true;
      dataUrl: string;
    }
  | {
      success: false;
      error: string;
    };

interface ExportBehaviors {
  exporting: ExportMode;
  foreignObjectRoots: Map<SVGForeignObjectElement, Root>;

  renderToSVG(respectSelection?: boolean): string;
  renderToPng(
    area: Rect,
    options?: {
      width?: number;
      filter?: (obj: fabric.Object) => boolean;
    }
  ): RenderToPNGResult;
  renderToHTML(options?: { forScreenshot?: boolean }): SVGElement;
  cleanupForeignObjectRoots(): void;
  renderExternalContentToSVG(
    ctx: any,
    object: fabric.Object
  ): SVGForeignObjectElement | undefined;
  recursivelyRenderExternalContent(
    ctx: CanvasRenderingContext2D,
    group: fabric.Object
  ): void;

  usedPortion(onlySelection?: boolean, accountForText?: boolean): Rect;
  rectContainingObjects(objects: fabric.Object[]): Rect;

  isHTMLExport(): boolean;
  showAhaViews(): boolean;

  waitForObjectsReady(objects?: fabric.Object[]): Promise<void>;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
function ExportBehaviors(Base: Constructor<WhiteboardCanvas>): any {
  return class extends Base implements ExportBehaviors {
    exporting: ExportMode = ExportMode.NONE;
    foreignObjectRoots = new Map<SVGForeignObjectElement, Root>();

    renderToSVG(respectSelection = true) {
      // Render the selected objects, or everything.
      let objects: fabric.Object[];
      const selection = this.getActiveObjects();
      const useSelection = respectSelection && selection.length > 0;
      if (useSelection) {
        objects = selection;
      } else {
        objects = this._chooseObjectsToRender();
      }
      const usedRect = this.usedPortion(useSelection);

      const ctx = new Context({
        width: usedRect.width,
        height: usedRect.height,
      });

      // Append the SVG canvas to the body so style lookups work
      document.body.appendChild(ctx.__canvas);

      this.exporting = ExportMode.SVG;
      this._renderObjects(ctx, objects);
      this.exporting = ExportMode.NONE;

      // Apply a viewBox to correctly offset the used portion of the canvas.
      const svgRoot = ctx.getSvg();
      svgRoot.setAttribute(
        'viewBox',
        `${usedRect.left} ${usedRect.top} ${usedRect.width} ${usedRect.height}`
      );
      const style = document.createElement('style');
      style.textContent = `@import url("https://fonts.googleapis.com/css2?family=Noto+Color+Emoji")`;
      svgRoot.appendChild(style);

      document.body.removeChild(ctx.__canvas);

      return ctx.getSerializedSvg();
    }

    renderToPng(
      area: Rect,
      options?: {
        width?: number;
        filter?: (obj: fabric.Object) => boolean;
      }
    ): { success: true; dataUrl: string } | { success: false; error: string } {
      // We have to stay below the maximum canvas size described here:
      // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/canvas#maximum_canvas_size
      // So set the multiplier based on the dimensions, if a width is not specified
      const multiplier = options?.width
        ? options?.width / area.width
        : Math.min(2, 15000 / Math.max(area.width, area.height));

      // Remove the zoom/pan before taking the snapshot.
      const originalTransform = this.viewportTransform;
      this.viewportTransform = fabric.iMatrix.slice(0) as TransformMatrix;

      this.exporting = ExportMode.PNG;
      this.requestRenderAll();

      const dataUrl = this.toDataURL({
        ...area,
        multiplier,
        format: 'png',
        filter: options?.filter,
      });

      this.exporting = ExportMode.NONE;
      this.requestRenderAll();

      this.viewportTransform = originalTransform;

      if (dataUrl === 'data:,') {
        return {
          success: false,
          error: 'Whiteboard is too large to export as a PNG',
        };
      }

      return {
        success: true,
        dataUrl,
      };
    }

    /**
     * Render a whiteboard as SVG interspersed with HTML (foreign objects)
     *
     * Same as rendering to SVG with the exception of specific shapes behaving differenly
     * when the export mode is HTML.
     *
     * It finds the first frame (presentation, then falls back to regular frames, then
     * the entire whiteboard) because this is used to generate the slides for
     * presentations, so we want to clip the slide contents to the bounds of the frame.
     */
    renderToHTML({ forScreenshot = false }): SVGElement {
      const objects = this._chooseObjectsToRender();

      // Find the first presentation frame, fall back to the first regular frame,
      // otherwise use the entire whiteboard for sizing
      const rootFrame =
        objects.find(object => isPresentationFrame(object)) ||
        objects.find(object => isFrame(object));

      const usedRect = rootFrame
        ? rootFrame.getBoundingRect(true, false)
        : this.usedPortion(false, forScreenshot);

      const ctx = new Context({
        width: usedRect.width,
        height: usedRect.height,
      });

      const originalSkipOffscreen = this.skipOffscreen;
      // Render everything during export, regardless of position.
      this.skipOffscreen = true;

      // Append the SVG canvas to the body so style lookups work
      document.body.appendChild(ctx.__canvas);

      this.exporting = forScreenshot
        ? ExportMode.HTML_WITHOUT_AHA_VIEWS
        : ExportMode.HTML;

      this._renderObjects(ctx, objects);
      this.exporting = ExportMode.NONE;

      // Apply a viewBox to correctly offset the used portion of the canvas.
      const svgRoot = ctx.getSvg() as SVGElement;
      svgRoot.setAttribute(
        'viewBox',
        `${usedRect.left} ${usedRect.top} ${usedRect.width} ${usedRect.height}`
      );

      this.skipOffscreen = originalSkipOffscreen;

      document.body.removeChild(ctx.__canvas);

      return svgRoot;
    }

    cleanupForeignObjectRoots(): void {
      this.foreignObjectRoots.forEach(root => {
        try {
          root.unmount();
        } catch (error) {
          console.warn('Failed to unmount React root:', error);
        }
      });
      this.foreignObjectRoots.clear();
    }

    /**
     * Renders an object with external content as a foreignObject element in SVG.
     * This provides proper z-ordering without the need for canvas carveouts.
     */
    renderExternalContentToSVG(
      ctx: any,
      object: fabric.Object
    ): SVGForeignObjectElement | undefined {
      if (!object.hasExternalContent()) return;

      const {
        left,
        top,
        width,
        height,
        scaleX = 1,
        scaleY = 1,
        angle = 0,
        strokeWidth = 0,
        stroke,
      } = object;

      try {
        const externalContent = object.renderExternalContent();

        const foreignObject = ctx
          .getSvg()
          .ownerDocument!.createElementNS(
            'http://www.w3.org/2000/svg',
            'foreignObject'
          );

        // Account for object border
        const hasStroke = strokeWidth > 0 && stroke && stroke !== 'transparent';
        const strokeOffset = hasStroke ? strokeWidth / 2 : 0;

        foreignObject.setAttribute('x', left + strokeOffset);
        foreignObject.setAttribute('y', top + strokeOffset);
        foreignObject.setAttribute('width', width * scaleX);
        foreignObject.setAttribute('height', height * scaleY);

        if (angle !== 0) {
          foreignObject.setAttribute(
            'transform',
            `rotate(${angle} ${left} ${top}) `
          );
          foreignObject.setAttribute('transform-origin', 'center');
        }

        const currentElement = ctx.__currentElement || ctx.getSvg();
        currentElement.appendChild(foreignObject);

        // Mount the React component to the foreignObject directly
        const root = createRoot(foreignObject);
        root.render(externalContent);
        this.foreignObjectRoots.set(foreignObject, root);

        return foreignObject;
      } catch (error) {
        console.warn('Failed to render object as foreignObject:', error);
      }
    }

    recursivelyRenderExternalContent(
      ctx: CanvasRenderingContext2D,
      group: fabric.Object
    ): void {
      const children = group.getObjects();
      children.forEach(child => {
        if (child.hasExternalContent()) {
          this.renderExternalContentToSVG(ctx, child);
        }

        if (isSimpleGroup(child)) {
          this.recursivelyRenderExternalContent(ctx, child);
        }
      });
    }

    // Return a rectangle describing the extend of the used portion of the
    // canvas.
    usedPortion(onlySelection = false, accountForText = true): Rect {
      const objects = onlySelection
        ? this.getActiveObjects()
        : this.getObjects();
      return this.rectContainingObjects(objects, accountForText);
    }

    rectContainingObjects(
      objects: fabric.Object[],
      accountForText = true
    ): Rect {
      // Find the outer dimensions of all objects.
      let points: { tl: IPoint; br: IPoint } | undefined;

      const expandPoints = (bb: Rect) => {
        if (!points) {
          points = {
            tl: { x: bb.left, y: bb.top },
            br: { x: bb.left + bb.width, y: bb.top + bb.height },
          };
        } else {
          const { tl, br } = points;
          if (bb.left < tl.x) tl.x = bb.left;
          if (bb.top < tl.y) tl.y = bb.top;
          if (bb.left + bb.width > br.x) br.x = bb.left + bb.width;
          if (bb.top + bb.height > br.y) br.y = bb.top + bb.height;
        }
      };

      for (const object of objects) {
        if (!object.visible) continue;

        expandPoints(object.getBoundingRect(true, true));

        // extend the bounding box if necessary to account for text content (except for frame titles)
        if (
          accountForText &&
          hasEmbeddedTextBehaviors(object) &&
          object.textContent
        ) {
          expandPoints(object.textPosition());
        }
      }

      if (!points) {
        return { left: 0, top: 0, width: 100, height: 100 };
      } else {
        const { tl, br } = points;
        return {
          left: tl.x,
          top: tl.y,
          width: br.x - tl.x,
          height: br.y - tl.y,
        };
      }
    }

    isHTMLExport(): boolean {
      return (
        this.exporting === ExportMode.HTML ||
        this.exporting === ExportMode.HTML_WITHOUT_AHA_VIEWS
      );
    }

    showAhaViews(): boolean {
      return !this.exporting || this.exporting === ExportMode.HTML;
    }

    /**
     * Returns whether an object is ready for capturing via PNG
     */
    isObjectReady(object: fabric.Object) {
      if (isSimpleGroup(object)) {
        let groupReady = true;
        object.forEachObjectDeep(child => {
          if (!this.isObjectReady(child)) groupReady = false;
        });
        return groupReady;
      } else if (isShapeImage(object)) {
        const { shape } = object;

        if (shape.loading || !shape.image?.complete) {
          return false;
        }
      } else if (hasEmbeddedTextBehaviors(object)) {
        if (object.textContent && object.textContent.length > 0) {
          if (
            !object.textImage ||
            !object.textImage.complete ||
            object.textImage.naturalHeight === 0
          ) {
            return false;
          }
        }
      }

      return true;
    }

    /**
     * Waits for all passed objects to be ready for capture. This is used for PNG
     * output, since we need images to be loaded, text to finish rendering async, etc.
     *
     * Checks every 100ms and waits a maximum of 10 seconds.
     */
    async waitForObjectsReady(objects = this.getObjects()) {
      return await new Promise<void>(resolve => {
        const startTime = Date.now();

        const checkImages = () => {
          const timeElapsed = Date.now() - startTime;
          if (timeElapsed > MAX_IMAGE_LOAD_WAIT_TIME) {
            console.warn(
              'PNG thumbnail generation timeout: Some images may not be fully loaded'
            );
            resolve();
            return;
          }

          const allShapesReady = objects.every(object =>
            this.isObjectReady(object)
          );

          if (allShapesReady) {
            resolve();
          } else {
            setTimeout(() => checkImages(), 100);
          }
        };

        checkImages();
      });
    }
  };
}

export default ExportBehaviors;
